import { useMsal } from '@azure/msal-react';

/**
 * The Entra app roles as they arrive in the token's `roles` claim. These are the app-role *values*
 * configured in the directory, which is what the claim carries — not the roles' display names.
 */
export const administratorRole = 'admin';

export const userRole = 'user';

export interface SignedInUser {
  /** Entra object id — the value an administrator registers a user under. */
  objectId: string | undefined;
  displayName: string | undefined;
  username: string | undefined;
  /**
   * Whether the ID token carries the administrator role. The API's own answer (`GET /api/tenant`)
   * is the one to prefer; this is the fallback for when that call was refused outright.
   */
  hasAdministratorRole: boolean;
  /**
   * Whether the ID token carries the `user` or `admin` role — the full tenant-facing surface, as
   * opposed to a bare `registration` submitter. Same fallback status as `hasAdministratorRole`.
   */
  hasFullAccessRole: boolean;
}

function readClaim(claims: unknown, name: string): string | undefined {
  if (typeof claims !== 'object' || claims === null || !(name in claims)) return undefined;
  const value = (claims as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : undefined;
}

function readRoles(claims: unknown): string[] {
  const roles = (claims as { roles?: string[] } | undefined)?.roles;
  return Array.isArray(roles) ? roles : [];
}

export function useSignedInUser(): SignedInUser {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const roles = readRoles(account?.idTokenClaims);

  return {
    // localAccountId is the oid for an Entra account; the claim is read first so the value shown
    // is literally the one the token carried.
    objectId: readClaim(account?.idTokenClaims, 'oid') ?? account?.localAccountId,
    displayName: account?.name,
    username: account?.username,
    hasAdministratorRole: roles.includes(administratorRole),
    hasFullAccessRole: roles.includes(userRole) || roles.includes(administratorRole),
  };
}
